import React, { useCallback, useEffect, useState } from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { saveAs } from 'file-saver';
import isEmpty from 'lodash/isEmpty';
import format from 'date-fns-old/format';

import Review from '../../Survey/Review';
import Loader from '../../common/Loader';
import ExpandableListItem from '../../ExpandableListItem';
import { SURVEY_RESULTS } from '../../../constants/strings';
import EmptyState from '../../common/ProgramPopup/EmptyState';
import { convertPreVisitFormData } from '../utils/preVisitForm';
import createSurveyPdfName from '../../../utils/createSurveyPdfName';
import { API_DATE_FORMAT } from '../../../utils/schedule/date-formatter';
import { getSurveyHeaderSubtitle } from '../../../utils/survey/getSpecification';

import { enhance } from './enhance';
import styles from '../index.styl';

const SurveysResults = ({
  date,
  hideTitle,
  className,
  targetUser,
  exportToPDF,
  targetUserId,
  locked = true,
  openedSurveyId,
  surveysLoading,
  exportPDFState,
  clearExportToPDF,
  surveys: programSurveys,
  intl: { formatMessage },
}) => {
  const { sending, succeed, blob } = exportPDFState || {};
  const getTargetSurveyAnswers = (surveys, surveyId) => {
    if (!surveyId) return surveys;

    const filteredSurveys = surveys.filter(survey => survey.id === surveyId);

    const hasNoAnswers =
      isEmpty(filteredSurveys) ||
      filteredSurveys.every(survey => isEmpty(survey.assignments));

    return hasNoAnswers ? [] : filteredSurveys;
  };

  if (surveysLoading) return <Loader />;
  if (!programSurveys) return <EmptyState centered={!!openedSurveyId} />;

  const answersToDisplay = getTargetSurveyAnswers(
    programSurveys,
    openedSurveyId,
  );

  if (isEmpty(answersToDisplay))
    return <EmptyState centered={!!openedSurveyId} />;

  const [exportName, setExportName] = useState(null);

  const exportPDFJob = useCallback(
    ({ openedAssignmentId, assignments, name }) => {
      const { datePDF } = getPDFData(openedAssignmentId, assignments);

      const params = {
        userId: targetUserId,
        assignmentId: openedAssignmentId,
        date: format(datePDF, API_DATE_FORMAT),
      };

      setExportName(
        createSurveyPdfName(name, targetUser.displayableName, params.date),
      );

      exportToPDF(params);
    },
    [targetUserId],
  );

  const getPDFData = useCallback((openedAssignmentId, assignmentsPDF) => {
    const { completedAt: datePDF } =
      assignmentsPDF.find(assignmentPDF => {
        return assignmentPDF.assignmentId.toString() === openedAssignmentId;
      }) || {};

    return { datePDF };
  }, []);

  useEffect(() => {
    const pdfLoaded = !sending && succeed;

    if (pdfLoaded && blob) {
      saveAs(blob, exportName);
      clearExportToPDF();
    }
  }, [exportPDFState, exportName]);

  return (
    <div className={className}>
      {answersToDisplay.map((survey, i) => {
        const {
          id,
          name,
          assigner,
          frequency,
          questions,
          scorecard,
          surveyType,
          assignments,
          description,
          assignmentMethod,
        } = survey;

        const userAppointment = get(assignments, '[0].userAppointment');
        const assignmentId = get(assignments, '[0].assignmentId');
        const list = convertPreVisitFormData(formatMessage, userAppointment);
        const subtitle = getSurveyHeaderSubtitle(
          surveyType,
          frequency,
          formatMessage,
          assignmentMethod,
          assigner,
        );

        const reviewOptions = {
          date,
          questions,
          scorecard,
          assignments,
          assignmentId,
          exportPDF: ({ openedAssignmentId }) =>
            exportPDFJob({ openedAssignmentId, assignments, name }),
        };

        return (
          <div key={`${i}-${id}`}>
            <If condition={!hideTitle}>
              <p className="fw6 f4 mb4">{formatMessage(SURVEY_RESULTS)}</p>
            </If>
            <ExpandableListItem
              data-id={id}
              locked={locked}
              opened={i === 0}
              expandableBlockClassName={styles.positionRelative}
              header={{
                list,
                subtitle,
                description,
                title: name,
                icon: 'surveys',
              }}
            >
              <Review {...reviewOptions} />
            </ExpandableListItem>
          </div>
        );
      })}
    </div>
  );
};

SurveysResults.propTypes = {
  date: PropTypes.string,
  locked: PropTypes.bool,
  surveys: PropTypes.array,
  hideTitle: PropTypes.bool,
  className: PropTypes.string,
  exportToPDF: PropTypes.func,
  targetUser: PropTypes.object,
  targetUserId: PropTypes.number,
  surveysLoading: PropTypes.bool,
  openedSurveyId: PropTypes.number,
  exportPDFState: PropTypes.object,
  clearExportToPDF: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
};

export default enhance(SurveysResults);



// WEBPACK FOOTER //
// ./src/components/Surveys/SurveysResults/index.jsx